import type { HeldRole, UserRoles } from "@shared/types";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useAuthStore } from "@/features/auth/store/auth.store";
import {
	type OtherRoleUnread,
	useNotificationsStore,
} from "@/features/settings/store/notifications.store";
import { queryKeys } from "@/lib/query-keys";
import { getMyRoles } from "../services/roles-api.service";

/** The unread count of every held role that is not active, by role. */
export function otherRoleUnreadFrom(roles: HeldRole[]): OtherRoleUnread {
	const counts: OtherRoleUnread = {};
	for (const held of roles) {
		if (!held.isActive) counts[held.role] = held.unreadCount;
	}
	return counts;
}

/**
 * The signed-in account's roles (GET /users/me/roles). Each time the list
 * arrives, the inactive roles' unread counts replace the notifications
 * store's `otherRoleUnread`, which realtime notifications then raise.
 */
export function useUserRoles(): UseQueryResult<UserRoles> {
	const { user } = useAuthStore();
	const setOtherRoleUnread = useNotificationsStore(
		(state) => state.setOtherRoleUnread,
	);
	const query = useQuery({
		queryKey: queryKeys.user.roles(),
		queryFn: getMyRoles,
		enabled: !!user?.userType,
	});

	const data = query.data;
	useEffect(() => {
		if (data) setOtherRoleUnread(otherRoleUnreadFrom(data.roles));
	}, [data, setOtherRoleUnread]);

	return query;
}

/** True when a role other than the active one has unread notifications. */
export function useHasOtherRoleUnread(): boolean {
	return useNotificationsStore((state) =>
		Object.values(state.otherRoleUnread).some((count) => (count ?? 0) > 0),
	);
}
